import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, fetchIntelReport } from '../api/games.js';
import type { IntelArchiveState } from '../console/model/intel-archive.js';

/**
 * The archive fetcher (M5 Q18).
 *
 * ## Why it lives here and not in the store
 *
 * Three constraints decide it between them, and none of them is taste:
 *
 *  - **The shell is presentational** (M5-D2): "no fetch, no socket, no token —
 *    those stay in the application, which is what makes the whole frame
 *    renderable from a fixture". A fetcher inside `console/` would need the seat
 *    token, and the token is the one thing the console must never hold.
 *  - **M5-D9 is about *interaction* state.** The store holds ~40 keys of what the
 *    operator has done — which tab, what is selected, **which turn they are
 *    reading**. A fetched report is not that: it is server data, on the same
 *    footing as `view` and `intel`, and it belongs beside them in `ws/`.
 *  - **Snapshot-independence cuts the other way too.** The store must survive the
 *    wholesale view swap; a cache of reports living in it would survive a *seat*
 *    switch as well, and one seat's report is precisely what another seat may not
 *    read. Held in a hook keyed on the token, it goes away with the seat that
 *    fetched it.
 *
 * So the **cursor** is store state (`intelTurn`) and the **reports** are here,
 * handed down as a `ReadonlyMap` prop exactly as `view` and `intel` are.
 *
 * ## One request per turn, and no retry loop
 *
 * A turn is fetched once. `idle → loading → ready | absent | forbidden | failed`
 * and nothing walks back on its own: a `failed` row is retried by the operator
 * pressing its control, which calls `request` again and puts that turn back
 * through the state machine. An automatic retry against a server that is refusing
 * is a console that looks busy and is doing nothing.
 *
 * There is deliberately no cache *across* seats or games: the map is state of
 * this hook instance and the effect below empties it whenever the token or the
 * game changes, because the route re-projects for the requesting seat on every
 * request and a report held past a seat switch would be the one thing the whole
 * alias scheme exists to prevent.
 */

export interface IntelArchive {
  readonly reports: ReadonlyMap<number, IntelArchiveState>;
  /** Ask for a turn. Idempotent while in flight; re-runs a `failed` one. */
  readonly request: (turn: number) => void;
}

const EMPTY: ReadonlyMap<number, IntelArchiveState> = new Map();

export function useIntelArchive(
  baseUrl: string,
  gameId: string,
  seatToken: string,
  enabled: boolean,
): IntelArchive {
  const [reports, setReports] = useState<ReadonlyMap<number, IntelArchiveState>>(EMPTY);

  /**
   * What has been asked for, held **outside** React state.
   *
   * The obvious implementation decides inside the `setReports` updater — read the
   * map, bail if the turn is already in flight, otherwise mark it `loading` and
   * set a flag the caller reads to start the fetch. It does not work, and it fails
   * *silently*: React invokes a functional updater during the **next render**, not
   * at the call site, so the flag is still `false` when the caller checks it and
   * the fetch is never started. Every row stays `idle`, the rail draws nothing
   * beside a turn number — which is a legitimate state — and the delta panel says
   * `NO PRIOR REPORT`, which is also a legitimate state. Nothing throws.
   *
   * Found in a browser on the second turn of a real game, and unreachable from a
   * component test: the tests assert that the reader *asks*, which it did.
   *
   * A ref is the right home for it anyway. "Has this turn been requested" is not
   * state anything renders — `reports` is what renders — and deriving one from the
   * other is what put the decision inside the updater in the first place.
   */
  const askedRef = useRef(new Map<number, IntelArchiveState['status']>());

  // Keyed on everything that changes what a report *means*. A report fetched for
  // one seat is not the same document as a report fetched for another, even for
  // the same turn — the projection re-aliases per observer.
  const key = `${baseUrl}|${gameId}|${seatToken}`;
  const keyRef = useRef(key);
  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    askedRef.current = new Map();
    setReports(EMPTY);
  }, [key]);

  const request = useCallback(
    (turn: number) => {
      if (!enabled) return;
      const held = askedRef.current.get(turn);
      // In flight, or already answered with something other than a failure. A
      // failure is re-runnable, because the retry is the operator's.
      if (held !== undefined && held !== 'failed') return;

      const settle = (state: IntelArchiveState): void => {
        askedRef.current.set(turn, state.status);
        setReports((previous) => new Map(previous).set(turn, state));
      };

      settle({ status: 'loading' });
      void fetchIntelReport(baseUrl, gameId, seatToken, turn)
        .then((report) => {
          settle({ status: 'ready', report });
        })
        .catch((error: unknown) => {
          settle(stateForError(error));
        });
    },
    [baseUrl, gameId, seatToken, enabled],
  );

  return { reports, request };
}

/**
 * The route is careful about its refusals, so this is careful about them too.
 *
 * 404 is *"that turn has not resolved yet"* — a statement about the game. 403 is
 * the umpire, who "reads the resolution log, not a report". Everything else is
 * the link, and the three must not collapse into one: an operator whose network
 * dropped and an operator looking at a turn that has not happened need different
 * next moves.
 */
function stateForError(error: unknown): IntelArchiveState {
  if (error instanceof ApiError) {
    if (error.status === 404) return { status: 'absent' };
    if (error.status === 403) return { status: 'forbidden' };
    return { status: 'failed', reason: error.code };
  }
  return { status: 'failed', reason: 'UNREACHABLE' };
}
